import React, { useEffect, useMemo, useState } from "react";
import { Activity, ExternalLink, Eye, Loader2, RefreshCw, Pin } from "lucide-react";
import { toast } from "sonner";
import { http, formatApiErrorDetail } from "../lib/api";
import { Button } from "./ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "./ui/alert-dialog";
import { useCategories } from "../context/CategoriesContext";
import { getColor } from "../lib/categoryColors";
import { cn } from "../lib/utils";

const RANGE_OPTIONS = [
  { value: 7, label: "7d" },
  { value: 30, label: "30d" },
  { value: 90, label: "90d" },
];

function formatNumber(n) {
  if (typeof n !== "number") return "0";
  if (n >= 1000) return `${(n / 1000).toFixed(n >= 10000 ? 0 : 1)}k`;
  return String(n);
}

export function AnalyticsTile({ onPinned } = {}) {
  const { categories } = useCategories();
  const [days, setDays] = useState(7);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [pinDialogOpen, setPinDialogOpen] = useState(false);
  const [pinning, setPinning] = useState(false);

  const load = async (window) => {
    setLoading(true);
    setError("");
    try {
      const { data: payload } = await http.get("/pdfs/stats", {
        params: { days: window, top_categories: 5 },
      });
      setData(payload);
    } catch (e) {
      setError(e?.response?.data?.detail || "Couldn't load analytics");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load(days);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [days]);

  const maxCatCount = useMemo(() => {
    const cats = data?.top_categories || [];
    return cats.reduce((m, c) => Math.max(m, c.count || 0), 0) || 1;
  }, [data]);

  const colorFor = (name) => {
    const c = categories.find((cat) => cat.name === name);
    return getColor(c?.color);
  };

  const isEmpty = !loading && !error && (data?.total || 0) === 0;
  const topCategory = data?.top_categories?.[0]?.name || null;

  const handlePin = async () => {
    if (!topCategory) return;
    setPinning(true);
    try {
      const { data: result } = await http.post("/pdfs/pin-top-category", {
        category: topCategory,
        days,
        limit: 3,
      });
      const pinnedCount = result?.pinned?.length || 0;
      const cleared = result?.newly_unfeatured || 0;
      toast.success(
        `Pinned ${pinnedCount} PDF${pinnedCount === 1 ? "" : "s"} from ${result.category} to Featured` +
          (cleared > 0 ? ` · cleared ${cleared} other${cleared === 1 ? "" : "s"}` : "")
      );
      setPinDialogOpen(false);
      if (typeof onPinned === "function") onPinned(result);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Couldn't pin top category");
    } finally {
      setPinning(false);
    }
  };

  return (
    <section
      className="rounded-2xl border border-slate-200 bg-white p-5 sm:p-6 mb-6"
      data-testid="admin-analytics-tile"
    >
      <header className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <div className="flex items-center gap-2.5 min-w-0">
          <span className="h-9 w-9 rounded-xl bg-brand-accent-soft text-brand-primary flex items-center justify-center shrink-0">
            <Activity className="h-4.5 w-4.5" />
          </span>
          <div className="min-w-0">
            <h2 className="font-display text-lg sm:text-xl font-bold text-slate-900 leading-tight">
              Last {days} days
            </h2>
            <p className="text-xs text-slate-500 leading-tight">
              What your visitors are clicking on Carrd.
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2 shrink-0">
          <div
            className="inline-flex rounded-full border border-slate-200 bg-white p-1 shadow-sm"
            data-testid="analytics-range-toggle"
          >
            {RANGE_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                type="button"
                onClick={() => setDays(opt.value)}
                className={cn(
                  "h-7 px-2.5 rounded-full text-xs font-bold tracking-wide transition-all",
                  days === opt.value
                    ? "bg-slate-900 text-white"
                    : "text-slate-500 hover:text-slate-900"
                )}
                data-testid={`analytics-range-${opt.value}`}
              >
                {opt.label}
              </button>
            ))}
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => load(days)}
            disabled={loading}
            className="h-8 px-2.5"
            data-testid="analytics-refresh"
            aria-label="Refresh analytics"
          >
            {loading ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <RefreshCw className="h-3.5 w-3.5" />
            )}
          </Button>
        </div>
      </header>

      {error ? (
        <div
          className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700"
          data-testid="analytics-error"
        >
          {error}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-[auto_1fr] gap-6 md:gap-10 items-start">
          {/* Big metrics */}
          <div
            className="flex items-stretch gap-4 sm:gap-6"
            data-testid="analytics-metrics"
          >
            <Metric
              icon={<ExternalLink className="h-3.5 w-3.5" />}
              label="Opens"
              value={data?.by_kind?.open ?? 0}
              loading={loading}
              testid="analytics-opens"
            />
            <div className="w-px bg-slate-100 self-stretch" />
            <Metric
              icon={<Eye className="h-3.5 w-3.5" />}
              label="Previews"
              value={data?.by_kind?.preview ?? 0}
              loading={loading}
              testid="analytics-previews"
            />
          </div>

          {/* Top categories breakdown */}
          <div className="min-w-0" data-testid="analytics-top-categories">
            <div className="flex items-center justify-between gap-3 mb-2">
              <span className="text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500">
                Top categories
              </span>
              {topCategory && (
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => setPinDialogOpen(true)}
                  className="h-7 px-2.5 text-xs gap-1 rounded-full border-brand-accent/60 text-brand-primary hover:bg-brand-accent-soft hover:border-brand-accent"
                  data-testid="analytics-pin-top"
                  title={`Promote top PDFs from ${topCategory} to Featured`}
                >
                  <Pin className="h-3 w-3" />
                  Pin {topCategory}
                </Button>
              )}
            </div>
            {isEmpty ? (
              <p className="text-sm text-slate-500 leading-relaxed">
                No clicks yet. Once visitors start using your library, you'll see
                which sports are pulling weight here.
              </p>
            ) : data?.top_categories?.length ? (
              <ul className="space-y-1.5" data-testid="analytics-top-list">
                {data.top_categories.map((cat) => {
                  const c = colorFor(cat.name);
                  const pct = Math.max(4, Math.round((cat.count / maxCatCount) * 100));
                  return (
                    <li
                      key={cat.name}
                      className="grid grid-cols-[auto_1fr_auto] items-center gap-2.5"
                      data-testid={`analytics-cat-${cat.name.toLowerCase().replace(/\s+/g, "-")}`}
                    >
                      <span
                        className={cn("h-2 w-2 rounded-full shrink-0", c.dot)}
                        style={c.dotStyle}
                      />
                      <div className="flex items-center gap-2 min-w-0">
                        <span className="text-sm font-semibold text-slate-700 truncate">
                          {cat.name}
                        </span>
                        <div className="hidden sm:block flex-1 h-1.5 rounded-full bg-slate-100 overflow-hidden">
                          <div
                            className={cn("h-full rounded-full", c.dot)}
                            style={{ width: `${pct}%`, ...(c.dotStyle || {}) }}
                          />
                        </div>
                      </div>
                      <span className="text-sm font-bold tabular-nums text-slate-900">
                        {formatNumber(cat.count)}
                      </span>
                    </li>
                  );
                })}
              </ul>
            ) : (
              <p className="text-sm text-slate-500">No category data yet.</p>
            )}
          </div>
        </div>
      )}

      <AlertDialog open={pinDialogOpen} onOpenChange={setPinDialogOpen}>
        <AlertDialogContent data-testid="analytics-pin-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display">
              Pin {topCategory} to Featured?
            </AlertDialogTitle>
            <AlertDialogDescription>
              We'll mark the top 3 most-clicked PDFs in <span className="font-semibold">{topCategory}</span>{" "}
              over the last {days} days as Featured, and clear the Featured flag from any PDF outside that set.
              You can always toggle individual PDFs back from the table below.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={pinning} data-testid="analytics-pin-cancel">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                handlePin();
              }}
              disabled={pinning}
              className="bg-brand-accent hover:bg-brand-accent-hover text-brand-primary font-bold"
              data-testid="analytics-pin-confirm"
            >
              {pinning ? <Loader2 className="h-4 w-4 animate-spin" /> : <Pin className="h-4 w-4" />}
              Pin top 3
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
}

function Metric({ icon, label, value, loading, testid }) {
  return (
    <div className="flex flex-col" data-testid={testid}>
      <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500">
        {icon}
        {label}
      </span>
      <span
        className={cn(
          "font-display text-3xl sm:text-4xl font-black text-slate-900 leading-none mt-1 tabular-nums",
          loading && "opacity-40"
        )}
      >
        {formatNumber(value)}
      </span>
    </div>
  );
}
